import { Button, Grid, Stack, Typography } from '@mui/material'
import { useEffect, useRef } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { consoleSessionHeld } from 'src/api/consoleSessionHeld'
import { GoogleTripError, googleTripFinish } from 'src/api/googleTrip'
import AnimatedLogo from 'src/components/AnimatedLogo'
import Page from 'src/components/layout/Page'
import { useLogin } from 'src/pages/LoginPage/parts/useLogin'

// ----------------------------------------------------------------------
// Where Google sends the browser back.
//
// The page hands the code and the state to the backend, which finishes the
// trip it holds for this browser, and does what the answer says: a sign-in
// signs in, a sign-in that owes the account's authenticator code asks for it
// on the login page, and a link is reported in Settings, where it started.
// A failure is said where the person can act on it: in Settings while
// somebody is signed in, on the login page otherwise.
//
// Google always sends back a code or an error. An address with neither was
// typed or bookmarked, and there is nothing to finish: the page says so where
// it stands rather than sending anybody anywhere.
// ----------------------------------------------------------------------

export const ACCOUNT_SETTINGS_PATH = '/admin/account/settings'

const DID_NOT_FINISH = 'Google did not complete the sign in. Try again.'

const GoogleOauthPage = () => {
  const { teslaSignInComplete } = useLogin()
  const navigate = useNavigate()
  const { search } = useLocation()
  const processedRef = useRef(false)
  const params = new URLSearchParams(search)
  const nothingToFinish = !params.get('code') && !params.get('error')

  useEffect(() => {
    // Once, including under strict mode's second effect.
    if (processedRef.current || nothingToFinish) {
      return
    }
    processedRef.current = true

    const returned = new URLSearchParams(search)
    const code = returned.get('code')
    const state = returned.get('state')
    const declined = returned.get('error')

    const refuse = async (message: string) => {
      if ((await consoleSessionHeld()) === true) {
        navigate(ACCOUNT_SETTINGS_PATH, {
          replace: true,
          state: { googleError: message },
        })
      } else {
        navigate('/login', { replace: true, state: { error: message } })
      }
    }

    void (async () => {
      if (declined || !code || !state) {
        await refuse(
          declined === 'access_denied'
            ? 'Google sign in was cancelled.'
            : DID_NOT_FINISH,
        )
        return
      }

      try {
        const outcome = await googleTripFinish(code, state)

        if (outcome.purpose === 'link') {
          navigate(ACCOUNT_SETTINGS_PATH, {
            replace: true,
            state: { googleLinked: outcome.linkedEmail },
          })
          return
        }

        if ('secondFactorTicket' in outcome) {
          navigate('/login', {
            replace: true,
            state: { googleSecondFactorTicket: outcome.secondFactorTicket },
          })
          return
        }

        // No console account uses it: the login page asks whether to make
        // one, and nothing is made until the answer is yes.
        if ('newAccountTicket' in outcome) {
          navigate('/login', {
            replace: true,
            state: {
              newAccount: {
                ticket: outcome.newAccountTicket,
                via: 'google',
                email: outcome.email,
              },
            },
          })
          return
        }

        // The same as any sign-in the backend answered with a session: the
        // invitation dealt with, then the console or onboarding.
        await teslaSignInComplete(
          outcome.token,
          outcome.invitation,
          outcome.invitation !== null,
        )
      } catch (failure) {
        await refuse(
          failure instanceof GoogleTripError ? failure.message : DID_NOT_FINISH,
        )
      }
    })()
  }, [search, navigate, teslaSignInComplete, nothingToFinish])

  if (nothingToFinish) {
    return (
      <Page title="Google sign in">
        <Grid
          container
          justifyContent="center"
          alignContent="center"
          style={{ minHeight: '100vh' }}
        >
          <Grid item xs={11} md={4}>
            <Stack spacing={2} alignItems="flex-start">
              <Typography variant="h2">Nothing to finish here</Typography>
              <Typography variant="body2" color="text.secondary">
                This page finishes a Google sign in, and Google did not send
                one. Start again from the sign in page.
              </Typography>
              <Button component={RouterLink} to="/login" variant="outlined">
                Go to sign in
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Page>
    )
  }

  return (
    <Page title="Login">
      <Grid
        container
        justifyContent="center"
        alignContent="center"
        style={{ minHeight: '100vh' }}
      >
        <Grid item xs={11} md={3}>
          <AnimatedLogo />
        </Grid>
      </Grid>
    </Page>
  )
}

export default GoogleOauthPage
